import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useMemo, useRef, useState } from 'react';

import { CoachSummary } from '@/components/CoachSummary';
import { trackEvent } from '@/modules/analytics';
import { createScheduledCall } from '@/modules/api';
import { useCoachAvailability, useRulesEngine } from '@/modules/rulesEngine';
import {
  CtaButton,
  LoadingScreen,
  Screen,
  type ScreenHandle,
  ScreenHeading,
  SelectableRowSkeleton,
} from '@/modules/ui';

import { DayRail } from './components/DayRail';
import { SlotRow } from './components/SlotRow';
import { groupTimeslots } from './utils/groupTimeslots';

// Kebab-safe testID segment from a slot's display label (e.g. "4:00 PM - 4:30 PM"
// → "4-00-pm-4-30-pm"). The raw ISO has `:` which is awkward for E2E selectors;
// only the active day's slots render at once, so a label-based id stays unique.
const slugify = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * COACH AVAILABILITY — book the initial consult with the selected coach
 * (Sparta's ProviderAvailabilityContainer). Fetches the coach's timeslots,
 * grouped by day. "Schedule Call" books the slot, assigns the primary coach,
 * and advances to the success screen; "Skip for now" assigns the coach and
 * skips ahead to the intro-message picker.
 */
export function CoachAvailabilityScreen() {
  const t = useT();
  const {
    matchedCoaches,
    selectedCoachUuid,
    setScheduledConsult,
    assignPrimaryCoach,
    setCurrentRoute,
    user,
  } = useRulesEngine();

  const coach =
    matchedCoaches?.coaches.find((c) => c.uuid === selectedCoachUuid) ?? matchedCoaches?.coaches[0];

  const { availability, isLoading } = useCoachAvailability(coach?.userUuid);

  const days = useMemo(
    () => groupTimeslots(availability?.timeslots ?? [], availability?.duration ?? 0),
    [availability]
  );

  const [selectedDayKey, setSelectedDayKey] = useState<string | undefined>();
  const [selectedSlotIso, setSelectedSlotIso] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [validationError, setValidationError] = useState(false);

  const screenRef = useRef<ScreenHandle>(null);

  const activeDayKey = selectedDayKey ?? days[0]?.key;
  const activeDay = days.find((d) => d.key === activeDayKey);
  const selectedSlot = activeDay?.slots.find((s) => s.iso === selectedSlotIso);

  const onSchedule = async () => {
    if (!coach) return;
    // The button stays enabled with no slot selected so the press surfaces a
    // validation message (scrolled into view) rather than silently no-op'ing.
    if (!selectedSlot) {
      setValidationError(true);
      screenRef.current?.scrollToTop();
      return;
    }
    setValidationError(false);
    setSubmitError(false);
    trackEvent('coach-availability-click-schedule', {
      uuid: user?.uuid,
      // Sparta sends the coach's USER uuid for coach events, not the profile uuid.
      coachUuid: coach.userUuid,
      timeslot: selectedSlot.iso,
    });
    try {
      setIsSubmitting(true);
      // Order mirrors Sparta's completeCoachMatchingEpic: assign the primary
      // coach to the team FIRST, then (only on its success) book the consult.
      // scheduledcall's `coach` is the coach's USER resource uri, not the
      // coachprofile resourceUri — sending the coachprofile uri 404s.
      await assignPrimaryCoach();
      await createScheduledCall({ coach: coach.userResourceUri, date: selectedSlot.iso });
      // Only derive an end time when the consult duration is known — otherwise
      // leave it off so the success screen shows just the start time (no range).
      const minutes = availability?.duration;
      const endDate = minutes
        ? new Date(new Date(selectedSlot.iso).getTime() + minutes * 60_000).toISOString()
        : undefined;
      setScheduledConsult({ date: selectedSlot.iso, endDate });
      setCurrentRoute('/step/coach-matching-success');
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  const onSkip = async () => {
    if (!coach) return;
    trackEvent('coach-availability-click-skip', { uuid: user?.uuid, coachUuid: coach.userUuid });
    setSubmitError(false);
    try {
      setIsSubmitting(true);
      setScheduledConsult(undefined);
      await assignPrimaryCoach();
      // No consult booked → on to the quick-intro step (Split-gated in Sparta;
      // enabled by default here — see featureFlags note).
      setCurrentRoute('/step/quick-intro-messages');
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  if (!coach) return <LoadingScreen />;

  return (
    <Screen
      ref={screenRef}
      busy={isSubmitting}
      footer={
        <VStack space='md'>
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onSchedule}
            testID='coach-availability-schedule'
          >
            <T _str='Schedule Call' />
          </CtaButton>
          <CtaButton
            type='secondary'
            isDisabled={isSubmitting}
            onPress={onSkip}
            testID='coach-availability-skip'
          >
            <T _str='Skip for now' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='xl'>
        <ScreenHeading title={t('Schedule your first video call')} />
        <CoachSummary coach={coach} />

        {validationError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Please select a date and time to continue' />
          </Text>
        ) : null}

        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong. Please try again.' />
          </Text>
        ) : null}

        {isLoading ? (
          <SelectableRowSkeleton count={6} rowHeight={72} />
        ) : days.length === 0 ? (
          <Text size='md' className='text-typography-700'>
            <T _str='No times are available right now — you can skip and schedule later.' />
          </Text>
        ) : (
          <VStack space='xl'>
            <DayRail
              days={days}
              selectedKey={activeDayKey}
              onSelect={(key) => {
                setSelectedDayKey(key);
                setSelectedSlotIso(undefined);
              }}
            />
            <VStack space='lg'>
              {activeDay?.slots.map((slot) => (
                <SlotRow
                  key={slot.iso}
                  label={slot.label}
                  selected={selectedSlotIso === slot.iso}
                  onPress={() => {
                    setSelectedSlotIso(slot.iso);
                    setValidationError(false);
                  }}
                  testID={`coach-availability-slot-${slugify(slot.label)}`}
                />
              ))}
            </VStack>
          </VStack>
        )}
      </VStack>
    </Screen>
  );
}
